import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import "./LobbyFortune.css";
import socket from "./socket";

const SECTOR_DEFINITIONS = [
  { kind: "empty" },
  { kind: "cash", amount: 1 },
  { kind: "ticket", face: 1, count: 1 },
  { kind: "cash", amount: 3 },
  { kind: "empty" },
  { kind: "ticket", face: 5, count: 1 },
  { kind: "cash", amount: 10 },
  { kind: "empty" },
  { kind: "ticket", face: 10, count: 1 },
  { kind: "cash", amount: 20 },
];

const ARC = 360 / SECTOR_DEFINITIONS.length;
const FULL_SPINS = 6;

const sectorCenter = (index) =>
  index * ARC + ARC / 2;

function splitSectorLabel(label) {
  const text = String(label || "").trim();
  if (!text) return [""];

  const amountMatch = text.match(/\$\s*\d+/i);
  if (amountMatch && text.length > 6) {
    const amount = amountMatch[0].replace(/\s+/g, "");
    const before = text.replace(amountMatch[0], "").trim();

    if (before) {
      return [before, amount];
    }
  }

  const words = text.split(/\s+/).filter(Boolean);
  if (words.length >= 3) {
    const middle = Math.ceil(words.length / 2);
    return [
      words.slice(0, middle).join(" "),
      words.slice(middle).join(" "),
    ];
  }

  return [text];
}

function getLabelFontSize(label, compact = false) {
  const length = String(label || "").trim().length;

  if (compact) {
    if (length >= 16) return 9;
    if (length >= 11) return 10;
    return 11;
  }

  if (length >= 16) return 11;
  if (length >= 11) return 12;
  return 13;
}

function getOuterLabelStyle(index, compact = false) {
  const radius = compact ? 132 : 168;
  const angle = sectorCenter(index) - 90;

  return {
    position: "absolute",
    left: "50%",
    top: "50%",
    width: 0,
    height: 0,
    transform: `rotate(${angle}deg) translate(${radius}px)`,
    transformOrigin: "0 0",
    pointerEvents: "none",
  };
}

function getInnerLabelStyle(label, compact = false) {
  const fontSize = getLabelFontSize(label, compact);

  return {
    width: compact ? 72 : 88,
    transform: "translate(-50%, -50%) rotate(90deg)",
    transformOrigin: "center center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: compact ? 1 : 2,
    textAlign: "center",
    fontSize,
    fontWeight: 700,
    lineHeight: compact ? 1.02 : 1.05,
    color: "#ffffff",
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    textShadow: "0 1px 2px rgba(0, 0, 0, 0.75)",
    letterSpacing: compact ? "0.01em" : "0.02em",
  };
}

export default function LobbyFortune({
  username,
  level = 1,
  vipTier = 0,
  minLevel = 1,
  minVip = 1,
}) {
  const { t: translate } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [angle, setAngle] = useState(0);
  const [result, setResult] = useState(null);
  const [srvStatus, setSrvStatus] = useState({
    eligible: false,
    reason: "",
    nextAvailableAt: null,
  });

  const sectors = useMemo(
    () =>
      SECTOR_DEFINITIONS.map((sector) => {
        if (sector.kind === "empty") {
          return {
            ...sector,
            label: translate("fortune.sectors.empty"),
          };
        }

        if (sector.kind === "ticket") {
          return {
            ...sector,
            label: translate("fortune.sectors.ticket", {
              amount: sector.face,
            }),
          };
        }

        return {
          ...sector,
          label: `$${sector.amount}`,
        };
      }),
    [translate]
  );

  const hint = translate("fortune.requiresLevelOrVip", {
    level: minLevel,
    vip: minVip,
  });

  const serverEligible = srvStatus.eligible;

  const localizeServerReason = (rawReason) => {
    const reason = String(rawReason || "").trim();

    if (!reason) {
      return serverEligible
        ? ""
        : translate("fortune.unavailableToday");
    }

    const normalized = reason.toLowerCase();

    if (
      normalized.includes("раз в сутки") ||
      normalized.includes("once per day") ||
      normalized.includes("una volta al giorno") ||
      normalized.includes("une fois par jour")
    ) {
      return translate("fortune.dailyLimit");
    }

    if (
      normalized.includes("требуется уровень") ||
      normalized.includes("requires level") ||
      normalized.includes("niveau") ||
      normalized.includes("livello")
    ) {
      return hint;
    }

    if (
      normalized.includes("сервер недоступен") ||
      normalized.includes("server unavailable") ||
      normalized.includes("serveur indisponible") ||
      normalized.includes("server non disponibile")
    ) {
      return translate("fortune.serverUnavailable");
    }

    if (
      normalized.includes("недоступно сегодня") ||
      normalized.includes("unavailable today") ||
      normalized.includes("indisponible aujourd") ||
      normalized.includes("non disponibile oggi")
    ) {
      return translate("fortune.unavailableToday");
    }

    return reason;
  };

  const localizePrizeLabel = (rawLabel, index) => {
    const label = String(rawLabel || "").trim();
    const normalized = label.toLowerCase();

    if (!label || normalized === "приз" || normalized === "prize") {
      return sectors[index]?.label || translate("fortune.prize");
    }

    if (
      normalized === "пусто" ||
      normalized === "empty" ||
      normalized === "vide" ||
      normalized === "vuoto"
    ) {
      return translate("fortune.sectors.empty");
    }

    const ticketMatch = label.match(
      /(?:билет|ticket|billet|biglietto).*?\$?\s*(\d+)/i
    );

    if (ticketMatch) {
      return translate("fortune.sectors.ticket", {
        amount: ticketMatch[1],
      });
    }

    return label;
  };

  const serverHint = localizeServerReason(srvStatus.reason);

  const API = `${process.env.REACT_APP_API_URL}/api/fortune`;
  const API_BASE = `${process.env.REACT_APP_API_URL}/api`;

  const refreshBalance = () => {
    const name = username || localStorage.getItem("username");

    if (name) {
      socket.emit("getUserBalance", {
        username: name,
      });
    }
  };

  const refreshProfileBalance = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(`${API_BASE}/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok && typeof data.balance === "number") {
        window.dispatchEvent(
          new CustomEvent("balance-updated", {
            detail: {
              balance: Number(data.balance),
              source: "profile",
            },
          })
        );
      }
    } catch (error) {
      console.warn("[fortune] refreshProfileBalance failed:", error);
    }
  };

  const refreshAssets = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(`${API_BASE}/user/assets`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        window.dispatchEvent(
          new CustomEvent("assets-updated", {
            detail: data,
          })
        );
      }
    } catch (error) {
      console.warn("[fortune] refreshAssets failed:", error);
    }
  };

  function normalizePrize(serverPrize, index) {
    const sector = sectors[index] || {};
    const prize = serverPrize || {};

    return {
      kind: String(prize.kind || sector.kind || "").toLowerCase(),
      amount: Number(prize.amount ?? sector.amount ?? prize.value ?? 0),
      face: Number(prize.face ?? sector.face ?? prize.value ?? 0),
      count: Number(prize.count ?? sector.count ?? 1),
      label:
        localizePrizeLabel(prize.label, index) ||
        sector.label ||
        translate("fortune.prize"),
    };
  }

  const renderSectorLabel = (sector, index, compact = false) => {
    const lines = splitSectorLabel(sector.label);

    return (
      <div
        key={`${compact ? "peek" : "full"}-${index}`}
        className={compact ? "fortune-peek-label" : "fortune-label"}
        style={getOuterLabelStyle(index, compact)}
      >
        <div style={getInnerLabelStyle(sector.label, compact)}>
          {lines.map((line, lineIndex) => (
            <span key={`${index}-${lineIndex}`}>{line}</span>
          ))}
        </div>
      </div>
    );
  };

  const onSpin = async () => {
    if (spinning) return;
    if (!serverEligible) return;

    setResult(null);
    setSpinning(true);

    try {
      const response = await fetch(`${API}/spin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username }),
      });

      const data = await response.json();

      if (!response.ok) {
        setSpinning(false);
        setSrvStatus({
          eligible: false,
          reason: data?.error || translate("fortune.genericError"),
        });
        return;
      }

      const index = Number(data.index ?? 0);
      const stopAngle = FULL_SPINS * 360 + (360 - sectorCenter(index));

      requestAnimationFrame(() => {
        setAngle(Math.round(stopAngle));
      });

      setTimeout(() => {
        setSpinning(false);

        const normalizedPrize = normalizePrize(data.prize, index);

        setResult({
          label:
            normalizedPrize.label ||
            sectors[index]?.label ||
            translate("fortune.prize"),
        });

        fetchStatus();

        const applied = data?.applied || {};

        if (applied.type === "money" && Number(applied.amount) > 0) {
          window.dispatchEvent(
            new CustomEvent("balance-updated", {
              detail: {
                add: Number(applied.amount),
              },
            })
          );
        }

        refreshBalance();
        refreshProfileBalance();

        if (applied.type === "ticket") {
          refreshAssets();
        }
      }, 5200);
    } catch (error) {
      console.error("[fortune/spin]", error);
      setSpinning(false);
      setSrvStatus({
        eligible: false,
        reason: translate("fortune.serverUnavailable"),
        nextAvailableAt: null,
      });
    }
  };

  async function fetchStatus() {
    try {
      const response = await fetch(
        `${API}/status?username=${encodeURIComponent(username)}`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || translate("fortune.statusError"));
      }

      setSrvStatus(data);
    } catch (error) {
      console.error("[fortune/status]", error);
      setSrvStatus({
        eligible: false,
        reason: translate("fortune.serverUnavailable"),
        nextAvailableAt: null,
      });
    }
  }

  React.useEffect(() => {
    if (isOpen) {
      fetchStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, username]);

  return (
    <>
      {!isOpen && (
        <div className="fortune-peek-wrapper">
          <div
            className="fortune-peek"
            onClick={() => setIsOpen(true)}
            role="button"
            tabIndex={0}
            aria-label={translate("fortune.openAria")}
            title={translate("fortune.title")}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setIsOpen(true);
              }
            }}
          >
            <div className="fortune-peek-pointer" />

            <div className="fortune-peek-wheel">
              <div className="fortune-peek-inner">
                {sectors.map((sector, index) =>
                  renderSectorLabel(sector, index, true)
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <div
          className="fortune-overlay"
          onClick={() => !spinning && setIsOpen(false)}
        >
          <div
            className="fortune-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="fortune-close"
              onClick={() => !spinning && setIsOpen(false)}
              aria-label={translate("fortune.close")}
              title={translate("fortune.close")}
            >
              ×
            </button>

            <div className="fortune-head">
              <h3 className="fortune-title">{translate("fortune.title")}</h3>
            </div>

            <div className="fortune-content">
              <div className="fortune-wheel">
                <div className="fortune-rim" />

                <div
                  className="fortune-rotor"
                  style={{
                    transform: `rotate(${angle}deg)`,
                    transition: spinning
                      ? "transform 5.2s cubic-bezier(.12,.9,.25,1)"
                      : "none",
                  }}
                >
                  {sectors.map((sector, index) =>
                    renderSectorLabel(sector, index, false)
                  )}
                </div>

                <div className="fortune-cap" />
                <div className="fortune-pointer" />
              </div>

              <div className="fortune-actions">
                <button
                  type="button"
                  className="spin-btn"
                  disabled={!serverEligible || spinning}
                  onClick={onSpin}
                >
                  {spinning
                    ? translate("fortune.spinning")
                    : translate("fortune.spin")}
                </button>

                {!serverEligible && !spinning && (
                  <div className="fortune-hint">{serverHint}</div>
                )}

                {result && (
                  <>
                    <div className="fortune-msg">{translate("fortune.yourPrize")}</div>
                    <div className="fortune-prize">{result.label}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
